import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DayViewProps, IEvent } from '../core/type';
import { EventBar } from './eventbar';
import { OverlayTrigger } from 'react-bootstrap';
import { renderTooltip, isMultiDayEvent as checkIsMultiDayEvent, getEventStartDateTime, getEventCategoryColor } from '../utils';
import { RootState } from '../../../../../redux/store';
import { useSelector } from 'react-redux';
import { getEventDates, normalizeDate } from '../core/_utility';
import { CustomDateFormate, getSaCurrentTime } from '../../../../../utils/date';

const CELL_HEIGHT = 80;
const MAX_VISIBLE_EVENTS = 10;
const MAX_VISIBLE_MULTI_DAY = 3;

interface IEventWithPosition extends IEvent {
    lane: number;
    position: {
        top: string;
        height: string;
        startMinutes: number;
        endMinutes: number;
        durationMinutes: number;
    };
}

const DayView: React.FC<DayViewProps> = ({ currentDate, events, onEventClick, onMoreClick }) => {
    const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
    const titleDisplayedRef = useRef<Set<string>>(new Set());
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { categoryAndColorsData } = useSelector((state: RootState) => state.eventReducer);

    const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

    useEffect(() => {
        titleDisplayedRef.current.clear();
    }, [events, currentDate]);

    useEffect(() => {
        if (scrollContainerRef.current) {
            const currentHour = Number(getSaCurrentTime().split(":")[0]);
            scrollContainerRef.current.scrollTop = Math.max(0, currentHour * CELL_HEIGHT - (CELL_HEIGHT * 2));
        }
    }, []);

    const isMultiDayEvent = useCallback((event: IEvent): boolean => checkIsMultiDayEvent(event), []);

    const isMultiHourEvent = useCallback((event: IEvent): boolean => {
        const { start, end } = getEventDates(event);
        return start && end && (end.getTime() - start.getTime()) / (1000 * 60 * 60) > 1;
    }, []);

    const shouldDisplayEventOnDate = useCallback((event: IEvent): boolean => {
        const { start, end } = getEventDates(event);

        if (!start || !end) return false;

        const currentDateObj = normalizeDate(currentDate)
        const eventStartDay = normalizeDate(start);
        const eventEndDay = normalizeDate(end);


        return currentDateObj >= eventStartDay && currentDateObj <= eventEndDay;
    }, [currentDate]);

    const dayEvents = useMemo(() => events.filter(shouldDisplayEventOnDate), [events, shouldDisplayEventOnDate]);
    const multiDayEvents = useMemo(() => dayEvents.filter(isMultiDayEvent), [dayEvents, isMultiDayEvent]);
    const singleDayEvents = useMemo(() => dayEvents.filter(ev => !isMultiDayEvent(ev)), [dayEvents, isMultiDayEvent]);

    const getEventStyleInPixels = useCallback((event: IEvent) => {
        const { start, end } = getEventDates(event);

        if (!start || !end) {
            return { top: '0px', height: '0px', startMinutes: 0, endMinutes: 0, durationMinutes: 0 };
        }

        const dayStart = normalizeDate(currentDate);
        const dayEnd = new Date(currentDate);
        dayEnd.setHours(23, 59, 59, 999);

        const effectiveStart = start > dayStart ? start : dayStart;
        const effectiveEnd = end < dayEnd ? end : dayEnd;
        const startMinutes = effectiveStart.getHours() * 60 + effectiveStart.getMinutes();
        const endMinutes = effectiveEnd.getHours() * 60 + effectiveEnd.getMinutes();
        const durationMinutes = endMinutes - startMinutes;

        return {
            top: `${(startMinutes / 60) * CELL_HEIGHT + 11}px`,
            height: `${(durationMinutes / 60) * CELL_HEIGHT}px`,
            startMinutes,
            endMinutes,
            durationMinutes
        };
    }, [currentDate]);

    const assignEventLanes = useCallback(
        (eventsInDay: IEvent[]) => {
            const sortedEvents = [...eventsInDay].sort((a, b) => {
                const aStart = getEventStartDateTime(a);
                const bStart = getEventStartDateTime(b);

                return (aStart?.getTime() || 0) - (bStart?.getTime() || 0);
            });

            // Precompute positions once
            const positionedEvents = sortedEvents.map((event) => ({
                event,
                position: getEventStyleInPixels(event),
            }));

            type PositionedEvent = {
                event: IEvent;
                position: ReturnType<typeof getEventStyleInPixels>;
            };

            const lanes: PositionedEvent[][] = [];

            const eventsWithLanes = positionedEvents.map(
                ({ event, position: eventPosition }) => {
                    let laneIndex = lanes.findIndex(
                        (lane) =>
                            !lane.some(({ position: ePosition }) => {
                                return (
                                    eventPosition.startMinutes <
                                    ePosition.endMinutes &&
                                    eventPosition.endMinutes >
                                    ePosition.startMinutes
                                );
                            })
                    );

                    if (laneIndex === -1) {
                        laneIndex = lanes.length;

                        lanes.push([
                            {
                                event,
                                position: eventPosition,
                            },
                        ]);
                    } else {
                        lanes[laneIndex].push({
                            event,
                            position: eventPosition,
                        });
                    }

                    return {
                        ...event,
                        lane: laneIndex,
                        position: eventPosition,
                    };
                }
            );

            return {
                eventsWithLanes,
                totalLanes: lanes.length,
            };
        },
        [getEventStyleInPixels]
    );

    const getTimeDisplay = useCallback((event: IEvent) => {
        const { start, end } = getEventDates(event);

        if (!start || !end) return '';
        return `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }, []);

    const toggleExpand = useCallback((key: string) => {
        setExpandedEvents(prev => {
            const newSet = new Set(prev);
            newSet.has(key) ? newSet.delete(key) : newSet.add(key);
            return newSet;
        });
    }, []);

    const formattedDate = useMemo(() => CustomDateFormate(currentDate), [currentDate]);

    const renderEventBlock = useCallback((event: IEventWithPosition, totalLanes: number, index: number) => {
        const lane = event.lane || 0;
        const laneWidth = 100 / totalLanes;
        const leftOffset = lane * laneWidth;
        const isMultiHour = isMultiHourEvent(event);
        const categoryColor = getEventCategoryColor(event.category, categoryAndColorsData);

        return (
            <OverlayTrigger
                key={event.id}
                placement="auto"
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip(event, categoryAndColorsData)}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: event.position.top,
                        height: `calc(${event.position.height} - 2px)`,
                        left: `${leftOffset}%`,
                        width: `calc(${laneWidth}% - 4px)`,
                        backgroundColor: `${categoryColor}25`,
                        borderLeft: `3px solid ${categoryColor}`,
                        borderRadius: '6px',
                        padding: isMultiHour ? '8px 6px' : '4px 6px',
                        cursor: 'pointer',
                        zIndex: 10 + index,
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                    onClick={() => onEventClick(currentDate, event)}
                    className="day-event-block"
                >
                    <div style={{
                        fontWeight: 600,
                        fontSize: isMultiHour ? '12px' : '11px',
                        marginBottom: isMultiHour ? '4px' : '2px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {event.title}
                    </div>
                    <div style={{ fontSize: isMultiHour ? '10px' : '9px', opacity: 0.8 }}>
                        {getTimeDisplay(event)}
                    </div>
                </div>
            </OverlayTrigger>
        );
    }, [currentDate, onEventClick, getTimeDisplay, isMultiHourEvent, categoryAndColorsData]);

    return (
        <div className="bg-white rounded-3" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="day-header text-center mb-4">
                <h3 className="fw-bold fs-3 mb-0 color-gray">{formattedDate}</h3>
            </div>

            <div className="day-view-content border border-2 rounded-4 overflow-hidden" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Multi-Day Events Section */}
                {multiDayEvents.length > 0 && (
                    <div className="border-bottom" style={{ backgroundColor: '#fafafa', flexShrink: 0 }}>
                        <div className="d-flex">
                            <div className="hour-label p-3 bg-gray-100 bg-opacity-50 fw-semibold text-gray-600 fs-8 border-end"
                                style={{ width: '80px', flexShrink: 0, backgroundColor: '#f9fafb' }}>
                                All Day
                            </div>
                            <div className="flex-grow-1 position-relative" style={{ minHeight: '60px', height: `${Math.max((Math.min(multiDayEvents.length, MAX_VISIBLE_MULTI_DAY) - 1 + 1) * 30, 60)}px` }}>
                                {multiDayEvents.slice(0, MAX_VISIBLE_MULTI_DAY).map((ev, idx) => {
                                    const { start, end } = getEventDates(ev);

                                    const eventStart = normalizeDate(start);
                                    const eventEnd = normalizeDate(end);
                                    if (!eventStart || !eventEnd) return null;

                                    const currentDay = normalizeDate(currentDate);
                                    const isFirstDay = currentDay.getTime() === eventStart as unknown as number;
                                    const isLastDay = currentDay.getTime() === eventEnd as unknown as number;

                                    return (
                                        <EventBar
                                            key={ev.id}
                                            event={ev}
                                            day={currentDate}
                                            lane={idx}
                                            rowHeight={22}
                                            isFirstDay={isFirstDay}
                                            isLastDay={isLastDay}
                                            titleDisplayedRef={titleDisplayedRef.current}
                                            onEventClick={(e: React.MouseEvent, event: IEvent) => onEventClick(currentDate, event)}
                                            viewType="day"
                                            currentDate={currentDate}
                                            showArrowIndicators={true}
                                        />
                                    );
                                })}
                                {multiDayEvents.length > MAX_VISIBLE_MULTI_DAY && (
                                    <div className='d-flex justify-content-center'>
                                        <button
                                            className='more-btn fs-9 text-primary bg-opacity-0 border-0 fw-semibold p-0'
                                            style={{ position: 'absolute', bottom: '4px', cursor: 'pointer', zIndex: 20 }}
                                            onClick={() => onMoreClick(currentDate)}
                                        >
                                            +{multiDayEvents.length - MAX_VISIBLE_MULTI_DAY} more
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Scrollable Time Grid */}
                <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', position: 'relative', minHeight: 0 }}>
                    <div style={{ position: 'relative' }}>
                        {hours.map(hour => (
                            <div key={hour} className="d-flex" style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <div style={{
                                    width: '80px', flexShrink: 0, padding: '12px 16px', borderRight: '1px solid #e5e7eb',
                                    backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center', height: `${CELL_HEIGHT}px`
                                }}>
                                    <span style={{ fontWeight: 600, fontSize: '12px', color: '#6b7280' }}>{hour.toString().padStart(2, '0')}:00</span>
                                    <span style={{ fontSize: '10px', color: '#9ca3af' }}>-</span>
                                    <span style={{ fontSize: '10px', color: '#9ca3af' }}>{(hour + 1).toString().padStart(2, '0')}:00</span>
                                </div>
                                <div style={{ flex: 1, height: `${CELL_HEIGHT}px`, borderRight: '1px solid #e5e7eb', backgroundColor: 'white', position: 'relative' }} />
                            </div>
                        ))}

                        {singleDayEvents.length > 0 && (() => {
                            const { eventsWithLanes, totalLanes } = assignEventLanes(singleDayEvents);
                            const dayKey = `day-${currentDate.toISOString()}`;
                            const isExpanded = expandedEvents.has(dayKey);
                            const displayEvents = isExpanded ? eventsWithLanes : eventsWithLanes.slice(0, MAX_VISIBLE_EVENTS);
                            const hasMore = eventsWithLanes.length > MAX_VISIBLE_EVENTS;

                            return (
                                <div style={{ position: 'absolute', top: 0, left: '80px', right: 0, bottom: 0, pointerEvents: 'none' }}>
                                    <div style={{ position: 'relative', height: '100%', pointerEvents: 'auto' }}>
                                        {displayEvents.map((event, idx) => renderEventBlock(event, totalLanes, idx))}
                                        {hasMore && !isExpanded && (
                                            <button
                                                style={{
                                                    position: 'sticky', bottom: '16px', left: '50%', transform: 'translateX(-50%)',
                                                    fontSize: '12px', padding: '6px 16px', background: 'white',
                                                    border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer',
                                                    zIndex: 30, fontWeight: 500, color: '#4b5563',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginTop: 'auto',
                                                    display: 'block', width: 'fit-content'
                                                }}
                                                onClick={() => toggleExpand(dayKey)}
                                            >
                                                +{eventsWithLanes.length - MAX_VISIBLE_EVENTS} more events
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DayView;